import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Chip,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  HolidayVillage,
  CheckCircle,
  Warning,
  Info,
  Send,
  Refresh,
  Category,
} from '@mui/icons-material';
import { holidaysAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2';

const HolidayCategories = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState(null);
  const [selections, setSelections] = useState({ A: [], B: [], C: [] });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadCategories();
  }, [year]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await holidaysAPI.getCategories(year);
      if (response.data.success) {
        setCategories(response.data.data.categories);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load holiday categories');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleHoliday = (category, holidayId) => {
    setSelections(prev => {
      const current = [...prev[category]];
      const index = current.indexOf(holidayId);
      
      if (index > -1) {
        current.splice(index, 1);
      } else {
        const max = categories[category]?.max || 0;
        if (current.length < max) {
          current.push(holidayId);
        } else {
          setError(`Maximum ${max} holidays allowed for ${categories[category]?.name}`);
          setTimeout(() => setError(''), 3000);
          return prev;
        }
      }
      
      return { ...prev, [category]: current };
    });
  };

  const validateSelections = () => {
    const errors = [];
    
    if (categories) {
      Object.keys(categories).forEach(cat => {
        const catData = categories[cat];
        const count = selections[cat].length;
        
        if (count > 0 && count < catData.min) {
          errors.push(`${catData.name}: Minimum ${catData.min} holidays required`);
        }
        if (count > catData.max) {
          errors.push(`${catData.name}: Maximum ${catData.max} holidays allowed`);
        }
        
        // Category C special rules
        if (cat === 'C' && count > 0) {
          const selectedHolidays = catData.holidays.filter(h => 
            selections[cat].includes(h.id)
          );
          
          // Rule: Max 2 from Astami to Dashami
          const astamiDashami = selectedHolidays.filter(h => 
            h.holiday_name.includes('Astami') || 
            h.holiday_name.includes('Nabami') || 
            h.holiday_name.includes('Dasami')
          );
          if (astamiDashami.length > 2) {
            errors.push('Category C: Maximum 2 holidays allowed from Astami to Dashami');
          }
          
          // Rule: Only one from Kali Puja, Diwali, Bhai Phota
          const exclusiveGroup = selectedHolidays.filter(h => 
            h.holiday_name.includes('Kali Puja') || 
            h.holiday_name.includes('Diwali') || 
            h.holiday_name.includes('Bhai Phota')
          );
          if (exclusiveGroup.length > 1) {
            errors.push('Category C: You can select only one from Kali Puja, Diwali, or Bhai Phota');
          }
        }
      });
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateSelections();
    
    if (errors.length > 0) {
      setError(errors.join('. '));
      return;
    }
    
    // Check if at least one category has selections
    const totalSelections = Object.values(selections).flat().length;
    if (totalSelections === 0) {
      setError('Please select at least one holiday');
      return;
    }
    
    const result = await Swal.fire({
      title: 'Submit Holiday Selections',
      text: `You have selected ${totalSelections} holiday(s). Submit for approval?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Submit',
      cancelButtonText: 'Cancel',
      background: 'rgb(10, 25, 41)',
      color: '#D4AF37',
      confirmButtonColor: '#D4AF37',
      cancelButtonColor: '#EF4444',
    });
    
    if (!result.isConfirmed) return;
    
    try {
      setSaving(true);
      setError('');
      
      const payload = [];
      Object.keys(selections).forEach(category => {
        selections[category].forEach(holidayId => {
          payload.push({ holiday_id: holidayId, category });
        });
      });
      
      const response = await holidaysAPI.submitCategorySelections(payload);
      
      if (response.data.success) {
        setSuccess('Holiday selections submitted successfully!');
        setTimeout(() => setSuccess(''), 5000);
        loadCategories();
      }
    } catch (err) {
      console.error('Error submitting selections:', err);
      setError(err.response?.data?.message || 'Failed to submit selections');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryColor = (cat) => {
    switch(cat) {
      case 'A': return '#2196F3';
      case 'B': return '#4CAF50';
      case 'C': return '#FF9800';
      default: return '#D4AF37';
    }
  };

  const renderCategory = (catKey) => {
    if (!categories || !categories[catKey]) return null;
    
    const cat = categories[catKey];
    const selectedCount = selections[catKey].length;
    const progress = (selectedCount / cat.max) * 100;
    
    return (
      <Card sx={{ 
        mb: 3, 
        bgcolor: 'rgb(10, 25, 41)', 
        border: `2px solid ${getCategoryColor(catKey)}`,
        borderRadius: 3,
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: getCategoryColor(catKey), fontWeight: 600 }}>
              <Category sx={{ mr: 1, verticalAlign: 'middle' }} />
              {cat.name}
            </Typography>
            <Chip 
              label={`${selectedCount}/${cat.max}`} 
              sx={{ 
                bgcolor: selectedCount >= cat.min ? '#4CAF50' : '#FF9800',
                color: '#FFF',
                fontWeight: 600
              }} 
            />
          </Box>
          
          <Typography variant="body2" sx={{ color: '#B4C4A8', mb: 1 }}>
            {cat.description}
          </Typography>
          
          {cat.rules && (
            <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(33, 150, 243, 0.1)', color: '#90CAF9' }}>
              {cat.rules.note}
            </Alert>
          )}
          
          <LinearProgress 
            variant="determinate" 
            value={Math.min(progress, 100)} 
            sx={{ 
              mb: 2, 
              height: 8, 
              borderRadius: 4,
              bgcolor: 'rgba(255,255,255,0.1)',
              '& .MuiLinearProgress-bar': { bgcolor: getCategoryColor(catKey) }
            }} 
          />
          
          <List>
            {cat.holidays.map((holiday) => {
              const isSelected = selections[catKey].includes(holiday.id);
              const isApproved = holiday.user_selection === 'approved';
              
              return (
                <ListItem
                  key={holiday.id}
                  sx={{
                    bgcolor: isSelected ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                    borderRadius: 2,
                    mb: 1,
                    border: isSelected ? '1px solid #D4AF37' : '1px solid transparent',
                  }}
                  secondaryAction={
                    holiday.user_selection && (
                      <Chip 
                        label={holiday.user_selection} 
                        size="small"
                        sx={{ 
                          bgcolor: isApproved ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                          color: isApproved ? '#81C784' : '#FFB74D',
                          fontSize: '0.75rem'
                        }} 
                      />
                    )
                  }
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleHoliday(catKey, holiday.id)}
                      disabled={isApproved || saving}
                      sx={{ 
                        color: '#D4AF37',
                        '&.Mui-checked': { color: '#D4AF37' }
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ color: isApproved ? '#81C784' : '#FFFFFF' }}>
                        {holiday.holiday_name}
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ color: '#B4C4A8' }}>
                        {holiday.holiday_date} ({holiday.day})
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#D4AF37' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#0A1929', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#D4AF37' }}>
            <HolidayVillage sx={{ mr: 1, verticalAlign: 'middle' }} />
            Holiday Selection - {year}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => { loadCategories(); setSelections({ A: [], B: [], C: [] }); }}
              sx={{ borderColor: '#D4AF37', color: '#D4AF37' }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={handleSubmit}
              disabled={saving}
              sx={{ bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#C19B2E' } }}
            >
              {saving ? 'Submitting...' : 'Submit Selections'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgb(10, 25, 41)', color: '#EF5350', border: '1px solid #EF5350' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, bgcolor: 'rgb(10, 25, 41)', color: '#81C784', border: '1px solid #81C784' }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Selection Summary */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
          <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>Selection Summary</Typography>
          <Grid container spacing={2}>
            {['A', 'B', 'C'].map(catKey => {
              if (!categories || !categories[catKey]) return null;
              const cat = categories[catKey];
              const count = selections[catKey].length;
              const isComplete = count >= cat.min;
              
              return (
                <Grid item xs={12} md={4} key={catKey}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: isComplete ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                    borderRadius: 2,
                    border: `1px solid ${isComplete ? '#4CAF50' : '#FF9800'}`
                  }}>
                    <Typography sx={{ color: '#D4AF37', fontWeight: 600 }}>{cat.name}</Typography>
                    <Typography sx={{ color: '#FFFFFF', fontSize: '1.5rem', fontWeight: 700 }}>
                      {count}/{cat.max}
                    </Typography>
                    <Typography sx={{ color: '#B4C4A8', fontSize: '0.875rem' }}>
                      Min: {cat.min}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>

        {/* Categories */}
        {renderCategory('A')}
        {renderCategory('B')}
        {renderCategory('C')}
      </Box>
    </Box>
  );
};

export default HolidayCategories;
